import { inject, injectable } from 'inversify';
import i18next from 'i18next';
import { decodeJWT } from '@aws-amplify/auth';

import type {
  AuthData,
  AuthorizeCode,
  ChangePassword,
  ChangePasswordWithOldPassword,
  CheckCode,
  CustomFormField,
  DeleteAccount,
  ExportAccountData,
  GenerateCode,
  GetCaptureStatus,
  GetCustomerConsents,
  GetFavorites,
  GetPublisherConsents,
  GetUser,
  GetWatchHistory,
  Login,
  Logout,
  Register,
  ResetPassword,
  SetAuthData,
  UpdateCaptureAnswers,
  UpdateCustomer,
  UpdateCustomerConsents,
  UpdateFavorites,
  UpdateWatchHistory,
} from '../../../../types/account';
import type { Config } from '../../../../types/config';
import AccountService from '../AccountService';
import { ACCESS_MODEL } from '../../../constants';
import { queryClient } from '../../../queryClient';
import { createURL } from '../../../utils/urlFormatting';
import { useConfigStore } from '../../../stores/ConfigStore';
import { argbToHex } from '../../../utils/common';

import StreamIQApiService from './StreamIQApiService';
import type {
  AccountObject,
  AccountsResponse,
  BookmarksResponse,
  CheckLoginResponse,
  CustomVariable,
  FavouritesListResponse,
  LineupResource,
  LoginResponse,
  Product,
  Profile,
  ProfilesResponse,
  QrCodeLoginResponse,
} from './types';
import StreamIQData from './StreamIQData';

const VD_TERMS_URL = 'https://www.iubenda.com/privacy-policy/50325189';
const QR_CODE_EXPIRY_SECONDS = 900;
const DEFAULT_PROFILE_NAME = 'Default';
const DEFAULT_PROFILE_IS_ADULT = true;

@injectable()
export default class StreamIQAccountService extends AccountService {
  accessModel = ACCESS_MODEL.AUTHVOD;
  svodOfferIds = [];
  sandbox = false;

  regionId: string | undefined = undefined;
  profileId: string | undefined = undefined;

  constructor(@inject(StreamIQApiService) protected readonly apiService: StreamIQApiService, @inject(StreamIQData) protected readonly data: StreamIQData) {
    super({
      landingEnabled: true,
      verificationPage: 'hosted',
      canUpdateEmail: false,
      canSupportEmptyFullName: false,
      canChangePasswordWithOldPassword: false,
      canRenewSubscription: false,
      canExportAccountData: false,
      canUpdatePaymentMethod: false,
      canShowReceipts: false,
      canDeleteAccount: false,
      paymentsPage: 'none',
      watchListSizeLimit: 48,
      canEditAboutYou: false,
      canEditSecurity: false,
      canEditTermsAndTracking: false,
      canEditRegistrationDetails: false,
    });
  }

  protected formatAccount(user: AccountObject) {
    return {
      id: user.id,
      uuid: user.id,
      email: user.username,
      fullName: '',
      firstName: '',
      lastName: '',
      metadata: {
        terms: true,
      },
      regDate: '',
      country: '',
      lastUserIp: '',
    };
  }

  protected getTermsConsent = (): CustomFormField => {
    const termsLink = `<a href="${VD_TERMS_URL}" target="_blank">${i18next.t('account:registration.terms_and_conditions')}</a>`;

    // t('account:registration.terms_consent')
    return {
      type: 'checkbox',
      isCustomRegisterField: true,
      showInProfile: false,
      required: true,
      name: 'terms',
      defaultValue: '',
      label: i18next.t('account:registration.terms_consent', { termsLink }),
      enabledByDefault: false,
      placeholder: '',
      options: {},
      version: '1',
    };
  };

  getRegionConfigVariables = async (regionId: string) => {
    const response = await this.apiService.get<{ resources: CustomVariable[] }>(createURL('/crm-service/customVariables', { regionId }));

    const siteName = response.resources.find((variable) => variable.key === 'siteName')?.value;
    const logo = response.resources.find((variable) => variable.key === 'logo')?.value;
    const mainColor = response.resources.find((variable) => variable.key === 'mainColor')?.value;
    const bgColor = response.resources.find((variable) => variable.key === 'bgColor')?.value;

    const highlightColor = mainColor?.startsWith('0x') ? argbToHex(mainColor) : mainColor;
    const backgroundColor = bgColor?.startsWith('0x') ? argbToHex(bgColor) : bgColor;

    return {
      siteName,
      logo,
      highlightColor,
      backgroundColor,
    };
  };

  protected setRegionResources = async () => {
    if (!this.regionId) return;

    const { siteName, logo, highlightColor, backgroundColor } = await this.getRegionConfigVariables(this.regionId);

    // get the lineup id
    const lineups = await this.apiService.get<{ resources: LineupResource[] }>(`/epg-service/lineups?regionId=${this.regionId}`);
    this.data.setLineup(lineups.resources[0]);

    useConfigStore.setState((state) => ({
      ...state,
      config: {
        ...state.config,
        siteName: siteName || state.config.siteName,
        assets: {
          ...state.config.assets,
          banner: logo || state.config.assets.banner,
        },
        styling: {
          ...state.config.styling,
          highlightColor: highlightColor || state.config.styling?.highlightColor,
          backgroundColor: backgroundColor || state.config.styling?.backgroundColor,
        },
      },
    }));
  };

  protected getRegionProducts = async () => {
    const response = await this.apiService.get<{ resources: Product[] }>(`/crm-service/products?regionId=${this.regionId}`);

    this.data.setProducts(response.resources);
  };

  protected getDefaultProfile = async (user: { id: string }) => {
    const profiles = await this.apiService.get<ProfilesResponse>(`/crm-service/profiles?accountId=${user.id}`, { withAuthentication: true });
    let profile = profiles.resources[0];

    // no profile exists, create one first
    if (!profile) {
      profile = await this.apiService.post<Profile>(
        '/crm-service/profiles',
        {
          name: DEFAULT_PROFILE_NAME,
          isAdult: DEFAULT_PROFILE_IS_ADULT,
        },
        { withAuthentication: true },
      );
    }

    this.profileId = profile.id;
  };

  initialize = async (_config: Config, _: string, logoutCallback: () => Promise<void>) => {
    this.apiService.initialize(logoutCallback);
  };

  login: Login = async ({ email, password }) => {
    try {
      const data = await this.apiService.post<LoginResponse>('/crm-service/logins', {
        flow: 'password',
        username: email,
        password,
      });

      this.apiService.setToken(data.idToken, data.refreshToken, data.idTokenExpiration);

      const user = this.formatAccount(data);
      const customerConsents = await this.getCustomerConsents({ customer: user });

      this.regionId = data.regionId;

      await Promise.allSettled([this.getDefaultProfile(user), this.getRegionProducts(), this.setRegionResources()]);

      return {
        isSignedIn: true,
        auth: {
          jwt: data.idToken,
          refreshToken: data.refreshToken,
        },
        user,
        customerConsents,
      };
    } catch {
      throw new Error('Failed to authenticate user.');
    }
  };

  getAuthData = async () => {
    if (await this.apiService.isAuthenticated()) {
      const credentials = await this.apiService.getToken();

      return {
        jwt: credentials.token,
        refreshToken: credentials.refreshToken,
      } as AuthData;
    }

    return null;
  };

  getUser: GetUser = async () => {
    try {
      const account = await this.apiService.get<AccountsResponse>('/crm-service/accounts', { withAuthentication: true });
      const user = this.formatAccount(account.resources[0]);
      const customerConsents = await this.getCustomerConsents({ customer: user });

      await this.getDefaultProfile(user);

      // only change config when the region has changed, this prevents updates when reloading the session
      if (this.regionId !== account.resources[0].regionId) {
        this.regionId = account.resources[0].regionId;
        await Promise.allSettled([this.getRegionProducts(), this.setRegionResources()]);
      }

      return {
        user,
        customerConsents,
      };
    } catch (error) {
      // failed to get user
      throw new Error('Failed to authenticate user');
    }
  };

  getWatchHistory: GetWatchHistory = async () => {
    const response = await this.apiService.get<BookmarksResponse>('/session-service/bookmarks?resolveReferences=true', {
      withAuthentication: true,
      headers: { 'X-Stream-Iq-Profile-Id': this.profileId ?? '' },
    });

    // store bookmark resources for re-use
    this.data.setBookmarks(response.resources);

    return this.data.getBookmarks().map((item) => ({
      mediaid: `${item.resource.type}_${item.resource.id}`,
      progress: item.positionInMs / item.contentDurationInMs,
    }));
  };

  getFavorites: GetFavorites = async () => {
    const response = await this.apiService.get<FavouritesListResponse>('/crm-service/favouriteListItems?resolveReferences=true', {
      withAuthentication: true,
      headers: { 'X-Stream-Iq-Profile-Id': this.profileId ?? '' },
    });

    // store favorite resources for re-use
    this.data.setFavorites(response.resources);

    return this.data.getFavorites().map(({ mediaid }) => ({ mediaid }));
  };
  updateWatchHistory: UpdateWatchHistory = async () => {
    // no-op the watch history is stored via the stop session request in the usePlayableMedia hook
  };
  updateFavorites: UpdateFavorites = async ({ favorites, user }) => {
    // ensure the favorites are synced to prevent adding duplicates
    await this.getFavorites({ user });

    const cachedFavorites = this.data.getFavorites();

    const existingItemsIds = cachedFavorites.map((item) => item.mediaid);
    const favoriteIds = favorites.map((e) => e.mediaid);

    // save new favorites
    await Promise.allSettled(
      favoriteIds.map((mediaId) => {
        const [type, id] = mediaId.split('_');
        // `tv-show` favorites are stored as an `epg-service/show` reference
        const referenceType = type === 'tv-show' ? 'show' : type;
        const serviceName = type === 'tv-show' ? 'epg-service' : type === 'show' || type === 'movie' ? 'vod-service' : 'epg-service';
        return !existingItemsIds.includes(mediaId)
          ? this.apiService.post(
              '/crm-service/favouriteListItems',
              { reference: { serviceName, type: referenceType, id }, ordinal: 0 },
              { withAuthentication: true, headers: { 'X-Stream-Iq-Profile-Id': this.profileId ?? '' } },
            )
          : Promise.resolve();
      }),
    );

    // delete removed favorites
    await Promise.allSettled(
      cachedFavorites.map((item) =>
        !favoriteIds.includes(item.mediaid)
          ? this.apiService.remove(`/crm-service/favouriteListItems/${item.id}`, {
              withAuthentication: true,
              headers: { 'X-Stream-Iq-Profile-Id': this.profileId ?? '' },
            })
          : Promise.resolve(),
      ),
    );

    // update cached favorites
    this.data.setFavorites(cachedFavorites.filter((item) => favoriteIds.includes(item.mediaid)));
  };

  updateCustomer: UpdateCustomer = async () => {
    throw new Error('Operation not supported');
  };

  register: Register = async () => {
    throw new Error('Operation not supported');
  };

  logout: Logout = async () => {
    await this.apiService.removeToken();

    // invalidate all queries except for the config
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'config-init' });
  };

  resetPassword: ResetPassword = async () => {
    throw new Error('Operation not supported');
  };

  changePasswordWithResetToken: ChangePassword = async () => {
    throw new Error('Operation not supported');
  };

  changePasswordWithOldPassword: ChangePasswordWithOldPassword = async () => {
    throw new Error('Operation not supported');
  };

  // consents and capture
  getPublisherConsents: GetPublisherConsents = async () => {
    return [this.getTermsConsent()];
  };
  getCustomerConsents: GetCustomerConsents = async ({ customer }) => {
    return [
      {
        name: 'terms',
        needsUpdate: false,
        state: customer.metadata.terms ? 'accepted' : 'declined',
        value: customer.metadata.terms ? 'true' : 'false',
        version: '1',
      },
    ];
  };

  updateCustomerConsents: UpdateCustomerConsents = async () => {
    // not needed until we add more (and optional) consents
    return [];
  };

  getCaptureStatus: GetCaptureStatus = async ({ customer }) => {
    return {
      isCaptureEnabled: true,
      shouldCaptureBeDisplayed: true,
      settings: [
        {
          answer: {
            firstName: customer.firstName || null,
            lastName: customer.lastName || null,
          },
          enabled: true,
          key: 'firstNameLastName',
          required: true,
        },
      ],
    };
  };
  updateCaptureAnswers: UpdateCaptureAnswers = async ({ customer, ...updatedAnswers }) => {
    return this.updateCustomer({ id: customer.id, ...updatedAnswers });
  };

  exportAccountData?: ExportAccountData | undefined;

  // optional features
  deleteAccount?: DeleteAccount | undefined;

  // tv login
  setAuthData: SetAuthData = async ({ authData }) => {
    const decoded = decodeJWT(authData.jwt);
    await this.apiService.setToken(authData.jwt, authData.refreshToken, decoded.payload.exp || -1);
  };

  generateCode: GenerateCode = async () => {
    const loginResponse = await this.apiService.post<QrCodeLoginResponse>('/crm-service/logins', { flow: 'qrCode' });

    return {
      id: loginResponse.id,
      code: loginResponse.id.slice(-4),
      expiresAt: Date.now() / 1000 + QR_CODE_EXPIRY_SECONDS,
      verificationUrl: loginResponse.url,
    };
  };

  authorizeCode?: AuthorizeCode | undefined;
  checkCode: CheckCode = async (params) => {
    if (!params.id) throw new Error('Failed to check code');

    try {
      const response = await this.apiService.get<CheckLoginResponse>(`/crm-service/logins/${params.id}`);

      if ('idToken' in response) {
        return {
          customerId: response.credentialId,
          refreshToken: response.refreshToken,
          accessToken: response.idToken,
          tokenType: 'JWT',
          expiresIn: response.expireAt,
        };
      }
    } catch {
      throw new Error('Failed to check code');
    }

    throw new Error('Failed to check code');
  };

  // optional
  getExternalLoginProviders: undefined;
  confirmLogin: undefined;
  loginExternal: undefined;
  getCustomerPortalUrl: undefined;
}
